import { api, ApiError } from '@/api/client';

export interface ContactRole {
  id: string;
  name: string;
}

export interface ContactUser {
  username: string;
  userEmail: string;
  userPhone?: string;
  isManagedByCompany: boolean;
}

export interface ContactInvite {
  id: string;
  sentAt: string;
  accepted: boolean;
}

export interface ContactDetails {
  canEdit: boolean;
  isEnabled: boolean;
  createdAt: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  isMainContact: boolean;
  user?: ContactUser;
  role?: ContactRole;
  invite?: ContactInvite;
}

export interface UpdateContactRequest {
  isEnabled?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  isMainContact?: boolean;
  username?: string;
}

export enum ContactUpdateResponse {
  OK = 'OK',
  EMAIL_EXISTS = 'EMAIL_EXISTS',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export async function fetchContactDetails(
  companyId: string,
  contactId: string
): Promise<ContactDetails> {
  return api.get<ContactDetails>(`/v1/companies/${companyId}/contacts/${contactId}`);
}

export async function updateContact(
  companyId: string,
  contactId: string,
  data: UpdateContactRequest
): Promise<ContactUpdateResponse> {
  try {
    await api.put(`/v1/companies/${companyId}/contacts/${contactId}`, data);
    return ContactUpdateResponse.OK;
  } catch (e) {
    const error = e as ApiError;

    if (error === null) {
      throw e;
    }

    if (error?.status === 409) {
      return ContactUpdateResponse.EMAIL_EXISTS;
    }

    return ContactUpdateResponse.UNKNOWN_ERROR;
  }
}

export async function toggleContactEnabled(
  companyId: string,
  contactId: string,
  isEnabled: boolean
): Promise<void> {
  await api.patch(`/v1/companies/${companyId}/contacts/${contactId}`, { type: isEnabled ? 'enable' : 'disable' });
}

export async function deleteContact(
  companyId: string,
  contactId: string
): Promise<void> {
  await api.delete(`/v1/companies/${companyId}/contacts/${contactId}`);
}
